import { AppTextField, AppRadioGroup } from '@/components/atoms';
import AppAdminFormLabel from '@/components/atoms/AppAdminFormLabel';
import RA2NDatePicker from '@/components/organisms/RA2NDatePicker/RA2NDatePicker';
import type { CreateRezeptSheetFormData } from '@/schemas/rezeptSheetSchema';
import { convGtJDate, dateValueToIso } from '@/utils/dateConverter';
import dayjs from 'dayjs';
import type { Control, FieldErrors, UseFormClearErrors } from 'react-hook-form';
import { Controller } from 'react-hook-form';

function isoToWareki(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const d = dayjs(dateStr);
  return d.isValid() ? convGtJDate(d) : '';
}

interface RezeptSheetFormFieldsProps {
  errors: FieldErrors<CreateRezeptSheetFormData>;
  control: Control<CreateRezeptSheetFormData>;
  clearErrors: UseFormClearErrors<CreateRezeptSheetFormData>;
}

const RezeptSheetFormFields = ({ errors, control, clearErrors }: RezeptSheetFormFieldsProps) => {
  const errorText = (msg?: string) =>
    msg ? <p className="mt-1 text-sm text-red-600">{msg}</p> : null;

  return (
    <>
      <div className="mb-4">
        <AppAdminFormLabel className="mb-2" label="用紙名" required />
        <Controller
          name="sheet_name"
          control={control}
          render={({ field }) => (
            <AppTextField
              {...field}
              value={field.value ?? ''}
              onChange={e => {
                field.onChange(e);
                if (errors.sheet_name) clearErrors('sheet_name');
              }}
              status={errors.sheet_name ? 'error' : undefined}
              className="w-full"
            />
          )}
        />
        {errorText(errors.sheet_name?.message)}
      </div>

      <div className="mb-4">
        <AppAdminFormLabel className="mb-2" label="サービス区分" required />
        <Controller
          name="service_type"
          control={control}
          render={({ field }) => (
            <AppRadioGroup
              value={field.value}
              onChange={e => {
                field.onChange(e.target.value);
                if (errors.service_type) clearErrors('service_type');
              }}
              options={[
                { label: 'はり・きゅう', value: 'A2' },
                { label: 'マッサージ', value: 'R2' },
              ]}
            />
          )}
        />
        {errorText(errors.service_type?.message)}
      </div>

      <div className="mb-4">
        <AppAdminFormLabel className="mb-2" label="用紙枚数" required />
        <Controller
          name="unit_amount"
          control={control}
          render={({ field }) => (
            <AppTextField
              {...field}
              value={field.value ?? ''}
              onChange={e => {
                field.onChange(e);
                if (errors.unit_amount) clearErrors('unit_amount');
              }}
              status={errors.unit_amount ? 'error' : undefined}
              className="w-full"
            />
          )}
        />
        {errorText(errors.unit_amount?.message)}
      </div>

      <div className="mb-4">
        <AppAdminFormLabel className="mb-2" label="用紙単価（税込）" required />
        <Controller
          name="price"
          control={control}
          render={({ field }) => (
            <AppTextField
              {...field}
              value={field.value ?? ''}
              onChange={e => {
                field.onChange(e);
                if (errors.price) clearErrors('price');
              }}
              status={errors.price ? 'error' : undefined}
              className="w-full"
            />
          )}
        />
        {errorText(errors.price?.message)}
      </div>

      <div className="mb-4">
        <AppAdminFormLabel className="mb-2" label="並び順" required />
        <Controller
          name="sortno"
          control={control}
          render={({ field }) => (
            <AppTextField
              {...field}
              value={field.value ?? ''}
              onChange={e => {
                field.onChange(e);
                if (errors.sortno) clearErrors('sortno');
              }}
              status={errors.sortno ? 'error' : undefined}
              className="w-full"
            />
          )}
        />
        {errorText(errors.sortno?.message)}
      </div>

      <div className="mb-4">
        <AppAdminFormLabel className="mb-2" label="適用期間" required />
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Controller
              name="available_from"
              control={control}
              render={({ field }) => (
                <RA2NDatePicker
                  value={isoToWareki(field.value)}
                  onChange={v => {
                    field.onChange(v ? dateValueToIso(v) ?? '' : '');
                    if (errors.available_from) clearErrors('available_from');
                    if (errors.available_to) clearErrors('available_to');
                  }}
                  placeholder=''
                  className="w-full"
                  error={!!errors.available_from}
                />
              )}
            />
            {errorText(errors.available_from?.message)}
          </div>
          <div>
            <Controller
              name="available_to"
              control={control}
              render={({ field }) => (
                <RA2NDatePicker
                  value={isoToWareki(field.value)}
                  onChange={v => {
                    field.onChange(v ? dateValueToIso(v) ?? '' : '');
                    if (errors.available_to) clearErrors('available_to');
                  }}
                  placeholder=''
                  className="w-full"
                  error={!!errors.available_to}
                />
              )}
            />
            {errorText(errors.available_to?.message)}
          </div>
        </div>
      </div>
    </>
  );
};

export default RezeptSheetFormFields;
